import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, Grid3x3, ShoppingCart, RefreshCw, Mail, LogOut, ExternalLink, Loader2, Menu, X, Store, Settings } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { LOGO_ROUND } from "../../mock";
import { useSeo } from "../../hooks/useSeo";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/sections", label: "Sections", icon: Grid3x3 },
  { to: "/admin/orders", label: "Sales & Labels", icon: ShoppingCart },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: RefreshCw },
  { to: "/admin/channels", label: "Sales Channels", icon: Store },
  { to: "/admin/email", label: "Mass Email", icon: Mail },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const AdminLayout = () => {
  const { user, loading, logout } = useAdminAuth();
  useSeo({ title: "Admin", noindex: true });
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // close the drawer whenever the route changes (mobile)
  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f0f7f2]"><Loader2 className="animate-spin text-[#2D6A4F]" size={32} /></div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const SidebarContent = (
    <>
      <div className="flex items-center gap-2 border-b border-white/10 px-5 py-5">
        <img src={LOGO_ROUND} alt="British Supplements" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20" />
        <div>
          <p className="font-display text-sm font-bold uppercase leading-none">British Supplements</p>
          <p className="mt-0.5 text-[11px] text-[#9fb6a8]">Admin Panel</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 font-display text-sm font-semibold uppercase tracking-wide transition ${isActive ? "bg-[#2D6A4F] text-white" : "text-[#b6c9bd] hover:bg-white/5"}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-1 border-t border-white/10 p-3">
        <button onClick={() => navigate("/")} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 font-display text-sm font-semibold uppercase tracking-wide text-[#b6c9bd] transition hover:bg-white/5">
          <ExternalLink size={18} /> View Store
        </button>
        <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 font-display text-sm font-semibold uppercase tracking-wide text-[#f0a0a0] transition hover:bg-white/5">
          <LogOut size={18} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#f0f7f2]">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col bg-[#102a1f] text-white lg:flex">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-[#102a1f] text-white shadow-2xl">
            <button onClick={() => setOpen(false)} aria-label="Close menu" className="absolute right-3 top-4 text-white/70 hover:text-white"><X size={22} /></button>
            {SidebarContent}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-[#e3efe7] bg-white px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)} aria-label="Open menu" className="text-[#1B4332]"><Menu size={24} /></button>
          <img src={LOGO_ROUND} alt="" className="h-8 w-8 rounded-full object-cover" />
          <span className="font-display text-sm font-bold uppercase text-[#1B4332]">Admin Panel</span>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
